// Upload for the phased burn tool.
//
// The flow: Export to Excel, edit the Burn Rate / Hours/Week / Crew Size rows
// in the sheet, then upload the sheet here. Nothing is written until the user
// has seen every cell that would change and the ETC each phase moves to, and
// pressed Apply. The sheet's other rows (Estimated Cost, Actual *, Between
// Date Interval) are derived or read-only and are ignored on the way in.
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { View, Text, Pressable } from '../lib/react-native-compat';
import ExcelJS from 'exceljs';
import moment from 'moment';
import { fNum, pNum } from '../db/company';

// The three rows of the export a person may edit, by the label the export
// prints in the Data Type column. Keys match the field names in userData.
const FIELDS = [
    { key: 'Rate', label: 'Burn Rate', exportDecimals: 2 },
    { key: 'Number_Hours', label: 'Hours/Week', exportDecimals: 0 },
    { key: 'Number_Units', label: 'Crew Size', exportDecimals: 2 },
];
const FIELD_BY_LABEL = Object.fromEntries(FIELDS.map(f => [f.label.toLowerCase(), f]));

const cellText = (v) => {
    if (v === null || v === undefined) return '';
    if (v instanceof Date) return moment.utc(v).format('YYYY-MM-DD');
    if (typeof v === 'object') {
        if (v.richText) return v.richText.map(r => r.text).join('');
        if (v.result !== undefined) return cellText(v.result);
        if (v.text !== undefined) return cellText(v.text);
        if (v.hyperlink) return cellText(v.text);
        return '';
    }
    return String(v).trim();
};

const weekFromHeader = (v) => {
    if (v instanceof Date) return moment.utc(v).format('YYYY-MM-DD');
    const t = cellText(v);
    if (!t) return null;
    const m = moment(t, ['YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss.SSSZ', 'M/D/YYYY', 'MM/DD/YYYY', 'M/D/YY'], true);
    return m.isValid() ? m.format('YYYY-MM-DD') : null;
};

const num = (v) => {
    if (v === null || v === undefined || v === '') return 0;
    const n = typeof v === 'number' ? v : pNum(String(v));
    return isNaN(n) ? 0 : n;
};
const round2 = (n) => Math.round(n * 100) / 100;
// pNum turns any text into a number; only text that looks like one may pass.
const NUMERIC = /^\s*\(?\s*-?\$?\s*[\d,]*\.?\d+\s*\)?\s*$/;

// Reads the export (or the Python template, which has the same columns) back
// into { phases: { code: { week: { Rate, Number_Hours, Number_Units } } } }.
// A field a row doesn't carry is left undefined so the diff keeps the current
// value for it. Returns warnings for anything it had to skip.
export const parseBurnToolPhasedWorkbook = async (arrayBuffer) => {
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(arrayBuffer);
    const warnings = [];
    let sheet = null, headerRowNo = 0, colOf = {}, weekCols = [];

    for (const ws of wb.worksheets) {
        for (let r = 1; r <= Math.min(ws.rowCount, 30) && !sheet; r++) {
            const row = ws.getRow(r);
            const names = {};
            row.eachCell({ includeEmpty: false }, (cell, c) => { names[cellText(cell.value).toLowerCase()] = c; });
            if (names['phase code'] && names['data type']) {
                sheet = ws; headerRowNo = r;
                colOf = { code: names['phase code'], type: names['data type'] };
                // The export's "ETC From Week" cell sits past the weeks on
                // the header row and holds a week date: a repeat of a week
                // is not a column (the first one is).
                const seenWeeks = new Set();
                row.eachCell({ includeEmpty: false }, (cell, c) => {
                    const wk = weekFromHeader(cell.value);
                    if (wk && !seenWeeks.has(wk)) { seenWeeks.add(wk); weekCols.push({ col: c, week: wk }); }
                });
            }
        }
        if (sheet) break;
    }
    if (!sheet) throw new Error('No sheet with "Phase Code" and "Data Type" columns was found. Upload a file made by Export to Excel.');
    if (!weekCols.length) throw new Error('No week columns (YYYY-MM-DD headers) were found on the sheet.');

    const phases = {};
    const seenTypes = {};
    for (let r = headerRowNo + 1; r <= sheet.rowCount; r++) {
        const row = sheet.getRow(r);
        const code = cellText(row.getCell(colOf.code).value);
        const typeLabel = cellText(row.getCell(colOf.type).value).toLowerCase();
        if (!code || !typeLabel) continue;
        const field = FIELD_BY_LABEL[typeLabel];
        if (!field) continue; // derived or actual rows: not editable
        if (seenTypes[code + '|' + field.key]) {
            warnings.push(`Row ${r}: ${code} has a second "${field.label}" row; the first one was used.`);
            continue;
        }
        seenTypes[code + '|' + field.key] = true;
        if (!phases[code]) phases[code] = {};
        weekCols.forEach(({ col, week }) => {
            const raw = row.getCell(col).value;
            const text = cellText(raw);
            if (!phases[code][week]) phases[code][week] = {};
            if (text === '') { phases[code][week][field.key] = 0; return; }
            const n = typeof raw === 'number' ? raw : NUMERIC.test(text) ? pNum(text) : NaN;
            if (isNaN(n)) {
                warnings.push(`Row ${r}, week ${week}: "${text}" is not a number and was skipped.`);
                return;
            }
            phases[code][week][field.key] = n;
        });
    }
    return { phases, weeks: weekCols.map(w => w.week), warnings };
};

// ETC per the tool's own rule: a week counts when all three values are set
// and the week is after the version's start week and before the completion
// date's week end.
const etcWeekInRange = (week, versionStart, completion) =>
    moment(week).isAfter(moment(versionStart).startOf('week')) &&
    (!completion || moment(week).isBefore(moment(completion).endOf('week')));

const etcOf = (vals, inRange) => {
    const r = num(vals?.Rate), h = num(vals?.Number_Hours), u = num(vals?.Number_Units);
    if (!inRange || !r || !h || !u) return { dollars: 0, hours: 0 };
    return { dollars: r * h * u, hours: h * u };
};

// Compares the parsed sheet to what the tool holds now.
//   userData:      the tool's userData map (phase '=' week -> { Rate, Number_Hours, Number_Units })
//   projectWeeks:  the week keys the tool knows
//   phaseCodes:    the phases on the job (array of codes)
//   versionStart:  Version_Start_DateTime of the picked version
//   completion:    the forecast completion date
export const diffBurnToolPhased = (parsed, { userData, projectWeeks, phaseCodes, versionStart, completion }) => {
    const knownWeeks = new Set(projectWeeks || []);
    const knownPhases = new Set((phaseCodes || []).map(String));
    const changes = [];       // one per changed cell: { phase, week, field, label, from, to }
    const finals = {};        // phase=week -> the full { Rate, Number_Hours, Number_Units } to save
    const warnings = [...(parsed.warnings || [])];
    const unknownPhases = [], unknownWeeks = new Set(), pastWeeks = new Set();

    Object.keys(parsed.phases).forEach((code) => {
        if (!knownPhases.has(code)) { unknownPhases.push(code); return; }
        Object.keys(parsed.phases[code]).forEach((week) => {
            if (!knownWeeks.has(week)) { unknownWeeks.add(week); return; }
            if (!moment(week).isAfter(moment(versionStart).startOf('week'))) { pastWeeks.add(week); return; }
            const key = code + '=' + week;
            const cur = userData?.[key] || {};
            const up = parsed.phases[code][week];
            const final = {};
            let changed = false;
            FIELDS.forEach((f) => {
                const curN = num(cur[f.key]);
                if (up[f.key] === undefined) { final[f.key] = curN; return; }
                const upN = up[f.key];
                // Unchanged if it equals the stored value, or equals what the
                // export printed for the stored value (Hours/Week prints whole).
                const printed = Number(curN.toFixed(f.exportDecimals));
                const same = round2(upN) === round2(curN) || round2(upN) === round2(printed);
                final[f.key] = same ? curN : upN;
                if (!same) { changed = true; changes.push({ phase: code, week, field: f.key, label: f.label, from: curN, to: upN }); }
            });
            if (changed) finals[key] = final;
        });
    });

    if (unknownPhases.length) warnings.push(`${unknownPhases.length} phase code${unknownPhases.length > 1 ? 's' : ''} not on this job skipped: ${unknownPhases.slice(0, 8).join(', ')}${unknownPhases.length > 8 ? '…' : ''}`);
    if (unknownWeeks.size) warnings.push(`${unknownWeeks.size} week column${unknownWeeks.size > 1 ? 's' : ''} not in this forecast skipped: ${[...unknownWeeks].sort().slice(0, 6).join(', ')}${unknownWeeks.size > 6 ? '…' : ''}`);
    if (pastWeeks.size) warnings.push(`${pastWeeks.size} week${pastWeeks.size > 1 ? 's' : ''} before the forecast week skipped (actuals cover them).`);

    // Per-phase ETC before and after, over every week the tool knows.
    const touched = [...new Set(changes.map(c => c.phase))];
    const perPhase = touched.map((code) => {
        let before = { dollars: 0, hours: 0 }, after = { dollars: 0, hours: 0 }, cells = 0, weeks = new Set();
        (projectWeeks || []).forEach((week) => {
            const key = code + '=' + week;
            const inRange = etcWeekInRange(week, versionStart, completion);
            const b = etcOf(userData?.[key], inRange);
            const a = etcOf(finals[key] || userData?.[key], inRange);
            before.dollars += b.dollars; before.hours += b.hours;
            after.dollars += a.dollars; after.hours += a.hours;
        });
        changes.forEach(c => { if (c.phase === code) { cells++; weeks.add(c.week); } });
        return { phase: code, cells, weeks: weeks.size, before, after };
    });
    const totals = perPhase.reduce((t, p) => ({
        before: { dollars: t.before.dollars + p.before.dollars, hours: t.before.hours + p.before.hours },
        after: { dollars: t.after.dollars + p.after.dollars, hours: t.after.hours + p.after.hours },
    }), { before: { dollars: 0, hours: 0 }, after: { dollars: 0, hours: 0 } });

    changes.sort((a, b) => a.phase.localeCompare(b.phase) || a.week.localeCompare(b.week) || FIELDS.findIndex(f => f.key === a.field) - FIELDS.findIndex(f => f.key === b.field));
    return { changes, finals, perPhase, totals, warnings, weeksTouched: new Set(changes.map(c => c.week)).size };
};

// The rows updateUserDataArray takes, one per changed week, shaped like the
// tool's own per-week save so the server treats them the same way.
export const buildBurnToolPhasedRows = (finals, { selectedJob, selectedVersion, lockedVersion, selectedJobData, completion }) =>
    Object.keys(finals).map((key) => {
        const [code] = key.split('=');
        const f = finals[key];
        const r = num(f.Rate), h = num(f.Number_Hours), u = num(f.Number_Units);
        return {
            active: 1,
            fieldName: 'row',
            tab: 'Burn Tool Labor Phased',
            rowId: key,
            costCode: code,
            version: selectedVersion + '',
            value: r * h * u,
            valueAmount: r * h * u,
            notes: '',
            projectStart: selectedJobData?.Planned_Start_Date,
            projectFinish: completion ? completion : selectedJobData?.Planned_Complete_Date,
            phaseStart: selectedJobData?.Phase_Start_Date,
            phaseFinish: completion ? completion : selectedJobData?.Phase_Complete_Date,
            rate: r,
            numberHours: h,
            numberUnits: u,
            hours: h * u,
            id: selectedJob,
            costType: '51100',
            lockedVersion: lockedVersion,
        };
    });

// ---- the preview dialog ----------------------------------------------------

const money = (n) => (n < 0 ? '-' : '') + '$' + fNum(Math.abs(n), 1);
const hrs = (n) => fNum(n, 1);
const signed = (n, fmt) => (n > 0 ? '+' : n < 0 ? '-' : '') + fmt(Math.abs(n));
const fieldText = (field, n) => n === 0 ? '—' : field === 'Rate' ? fNum(n, 2) : field === 'Number_Hours' ? fNum(n, 2) : fNum(n, 2);

const cardStyle = { backgroundColor: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, boxShadow: 'var(--shadow-xs)' };
const th = (label, w, right, key) => <Text key={key || label} style={{ width: w, fontSize: 12, fontWeight: '500', color: 'var(--muted-foreground)', textAlign: right ? 'right' : 'left', paddingRight: 8 }}>{label}</Text>;
const td = (text, w, right, extra) => <Text style={{ width: w, fontSize: 13, color: 'var(--foreground)', textAlign: right ? 'right' : 'left', paddingRight: 8, fontVariantNumeric: 'tabular-nums', ...(extra || {}) }}>{text}</Text>;

const Stat = ({ label, before, after, fmt }) => (
    <View style={{ ...cardStyle, padding: 12, paddingHorizontal: 16, flex: 1, minWidth: 180 }}>
        <Text style={{ fontSize: 12, color: 'var(--muted-foreground)', marginBottom: 2 }}>{label}</Text>
        <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <Text style={{ fontSize: 13, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums', textDecoration: 'line-through' }}>{fmt(before)}</Text>
            <Text style={{ fontSize: 22, fontWeight: '600', color: 'var(--foreground)', fontVariantNumeric: 'tabular-nums' }}>{fmt(after)}</Text>
            <Text style={{ fontSize: 13, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>{signed(after - before, fmt)}</Text>
        </View>
    </View>
);

const btn = (kind) => ({
    height: 32, paddingHorizontal: 12, borderRadius: 10, alignItems: 'center', justifyContent: 'center', flexDirection: 'row', cursor: 'pointer',
    ...(kind === 'primary'
        ? { backgroundColor: 'var(--primary)', border: '1px solid transparent' }
        : { backgroundColor: 'var(--background)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-xs)' }),
});

export const BurnToolPhasedUploadPreview = ({ fileName, result, lockedVersion, applying, onApply, onCancel }) => {
    const [showAll, setShowAll] = useState(false);
    const { changes, perPhase, totals, warnings, weeksTouched } = result;
    const LIMIT = 200;
    const shown = showAll ? changes : changes.slice(0, LIMIT);
    const canApply = changes.length > 0 && !lockedVersion && !applying;

    // On document.body: an ancestor in the shell carries a transform, which
    // would make the fixed overlay size to the content area instead of the window.
    return createPortal(
        <View style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000, backgroundColor: 'rgb(0 0 0 / 0.5)', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onCancel}>
            <View onClick={(e) => e.stopPropagation && e.stopPropagation()} style={{ ...cardStyle, boxShadow: 'var(--shadow-md)', width: '100%', maxWidth: 960, maxHeight: '90vh', overflow: 'hidden', flexDirection: 'column' }} role="dialog" aria-modal="true" aria-label="Review burn tool changes">
                <View style={{ padding: 14, paddingHorizontal: 16, borderBottom: '1px solid var(--border)', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <View>
                        <Text style={{ fontSize: 15, fontWeight: '600', color: 'var(--foreground)' }}>Review changes before updating the burn tool</Text>
                        <Text style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{fileName}</Text>
                    </View>
                    <Pressable onPress={onCancel} aria-label="Close" style={{ padding: 4, paddingHorizontal: 8, borderRadius: 10, cursor: 'pointer' }}>
                        <Text style={{ fontSize: 16, color: 'var(--text-secondary)' }}>×</Text>
                    </Pressable>
                </View>

                <View style={{ padding: 16, overflowY: 'auto', backgroundColor: 'var(--subtle)', gap: 12 }}>
                    {changes.length === 0 ? (
                        <Text style={{ fontSize: 14, color: 'var(--foreground)' }}>The sheet matches what the burn tool already holds. Nothing would change.</Text>
                    ) : (
                        <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
                            <View style={{ ...cardStyle, padding: 12, paddingHorizontal: 16, minWidth: 180 }}>
                                <Text style={{ fontSize: 12, color: 'var(--muted-foreground)', marginBottom: 2 }}>Cells that change</Text>
                                <Text style={{ fontSize: 22, fontWeight: '600', color: 'var(--foreground)', fontVariantNumeric: 'tabular-nums' }}>{changes.length}</Text>
                                <Text style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{perPhase.length} phase{perPhase.length === 1 ? '' : 's'}, {weeksTouched} week{weeksTouched === 1 ? '' : 's'}</Text>
                            </View>
                            <Stat label="ETC dollars, touched phases" before={totals.before.dollars} after={totals.after.dollars} fmt={money} />
                            <Stat label="ETC hours, touched phases" before={totals.before.hours} after={totals.after.hours} fmt={hrs} />
                        </View>
                    )}

                    {warnings.length > 0 && (
                        <View style={{ backgroundColor: 'var(--warning-tint)', border: '1px solid color-mix(in srgb, var(--warning) 25%, transparent)', borderRadius: 10, padding: 10, paddingHorizontal: 12 }}>
                            <Text style={{ fontSize: 12, fontWeight: '500', color: 'var(--warning-text)', marginBottom: 2 }}>Caution: skipped on the way in</Text>
                            {warnings.map((w, i) => <Text key={i} style={{ fontSize: 13, color: 'var(--warning-text)' }}>{w}</Text>)}
                        </View>
                    )}

                    {perPhase.length > 0 && (
                        <View style={{ ...cardStyle, overflow: 'hidden' }}>
                            <Text style={{ padding: 10, paddingHorizontal: 12, fontSize: 13, fontWeight: '600', color: 'var(--foreground)', borderBottom: '1px solid var(--border)' }}>By phase</Text>
                            <View style={{ flexDirection: 'row', backgroundColor: 'var(--subtle)', borderBottom: '1px solid var(--border)', padding: '6px 12px' }}>
                                {th('Phase', 90)}{th('Cells', 60, true)}{th('Weeks', 60, true)}{th('ETC $ now', 120, true)}{th('ETC $ after', 120, true)}{th('Change', 110, true, 'change-dollars')}{th('ETC h now', 100, true)}{th('ETC h after', 100, true)}{th('Change', 90, true, 'change-hours')}
                            </View>
                            {perPhase.map((p) => (
                                <View key={p.phase} style={{ flexDirection: 'row', padding: '5px 12px', borderBottom: '1px solid var(--border)' }}>
                                    {td(p.phase, 90, false, { fontWeight: '500' })}
                                    {td(p.cells, 60, true)}{td(p.weeks, 60, true)}
                                    {td(money(p.before.dollars), 120, true, { color: 'var(--text-secondary)' })}
                                    {td(money(p.after.dollars), 120, true)}
                                    {td(signed(p.after.dollars - p.before.dollars, money), 110, true)}
                                    {td(hrs(p.before.hours), 100, true, { color: 'var(--text-secondary)' })}
                                    {td(hrs(p.after.hours), 100, true)}
                                    {td(signed(p.after.hours - p.before.hours, hrs), 90, true)}
                                </View>
                            ))}
                        </View>
                    )}

                    {changes.length > 0 && (
                        <View style={{ ...cardStyle, overflow: 'hidden' }}>
                            <Text style={{ padding: 10, paddingHorizontal: 12, fontSize: 13, fontWeight: '600', color: 'var(--foreground)', borderBottom: '1px solid var(--border)' }}>Every cell that changes</Text>
                            <View style={{ flexDirection: 'row', backgroundColor: 'var(--subtle)', borderBottom: '1px solid var(--border)', padding: '6px 12px' }}>
                                {th('Phase', 90)}{th('Week', 110)}{th('Field', 110)}{th('Now', 100, true)}{th('After', 100, true)}
                            </View>
                            <View style={{ maxHeight: 320, overflowY: 'auto' }}>
                                {shown.map((c, i) => (
                                    <View key={i} style={{ flexDirection: 'row', padding: '4px 12px', borderBottom: '1px solid var(--border)' }}>
                                        {td(c.phase, 90, false, { fontWeight: '500' })}
                                        {td(c.week, 110)}
                                        {td(c.label, 110)}
                                        {td(fieldText(c.field, c.from), 100, true, { color: 'var(--text-secondary)' })}
                                        {td(fieldText(c.field, c.to), 100, true, { fontWeight: '600' })}
                                    </View>
                                ))}
                            </View>
                            {changes.length > LIMIT && !showAll && (
                                <Pressable onPress={() => setShowAll(true)} style={{ padding: 8, paddingHorizontal: 12, cursor: 'pointer' }}>
                                    <Text style={{ fontSize: 13, color: 'var(--primary)' }}>Show all {changes.length} cells</Text>
                                </Pressable>
                            )}
                        </View>
                    )}
                    <Text style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>Only the Burn Rate, Hours/Week and Crew Size rows are read. A blank cell clears that value. Estimated Cost, Actual and Between Date Interval rows are recomputed by the tool.</Text>
                </View>

                <View style={{ padding: 12, paddingHorizontal: 16, borderTop: '1px solid var(--border)', flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                    {lockedVersion && <Text style={{ fontSize: 13, color: 'var(--destructive-text)', marginRight: 'auto' }}>This version is locked. Nothing can be applied.</Text>}
                    <Pressable onPress={onCancel} style={btn('outline')}>
                        <Text style={{ fontSize: 14, fontWeight: '500', color: 'var(--foreground)' }}>Cancel</Text>
                    </Pressable>
                    <Pressable onPress={canApply ? onApply : null} disabled={!canApply} style={{ ...btn('primary'), opacity: canApply ? 1 : 0.5, cursor: canApply ? 'pointer' : 'not-allowed' }}>
                        <Text style={{ fontSize: 14, fontWeight: '500', color: 'var(--primary-foreground)' }}>{applying ? 'Applying…' : `Apply ${changes.length} change${changes.length === 1 ? '' : 's'}`}</Text>
                    </Pressable>
                </View>
            </View>
        </View>,
        document.body
    );
};
